
import { firstValueFrom } from 'rxjs';

import {
    DataService,
    NotificationService,
} from '@vendure/admin-ui/core';

import {
    Mutation,
    MutationExportOrdersToCsvArgs,
    MutationImportOrderFulfillmentsFromCsvArgs,
    MutationExportUnshippedOrdersToCsvArgs,
} from '../../common/ui/generated';

import {
    IMPORT_ORDER_FULFILLMENTS_FROM_CSV,
    EXPORT_UNSHIPPED_ORDERS_TO_CSV,
    EXPORT_ORDERS_TO_CSV,
    AUTO_ORDER_FULFILLMENTS,
    MARK_ORDERS_AS_DELIVERED,
} from './graphql';

import { downloadFile } from '../../common/ui/utils';

type OrderFulfillmentFormat = 'order-import-format-dealomega' | 'order-import-format-dealomega-universal';

export function onOrderFulfillmentImport(format: OrderFulfillmentFormat, dataService: DataService, notificationService: NotificationService) {
    // Create a hidden file input element programmatically
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.style.display = 'none';  // Ensure it stays hidden
    fileInput.accept = ".csv"

    // Append the input to the document body
    document.body.appendChild(fileInput);

    fileInput.addEventListener('change', async (event: Event) => {
        try {
            const input = event.target as HTMLInputElement;
            if (input.files && input.files.length > 0) {
                const file = input.files[0];
                await importOrderFulfillments(file, format, dataService, notificationService);
            }
        } catch (error: any) {
            notificationService.error('Error executing import: ' + error.message);
        }
    });

    // Programmatically click the file input to open the file dialog
    fileInput.click();

    // Remove the input after file selection to clean up
    fileInput.remove();
}

async function importOrderFulfillments(file: File, format: string, dataService: DataService, notificationService: NotificationService) {

    // const dataService = context.injector.get(DataService);
    dataService.mutate<
        Mutation,
        MutationImportOrderFulfillmentsFromCsvArgs
    >(
        IMPORT_ORDER_FULFILLMENTS_FROM_CSV, { file, format }
    ).subscribe({
        next: (response: Mutation) => {
            if (response.importOrderFulfillmentsFromCsv.error) {
                notificationService.error(response.importOrderFulfillmentsFromCsv.error);
            } else {
                notificationService.info(`Import done, imported ${response.importOrderFulfillmentsFromCsv.importedItems} order fulfillment(s)`);
            }
        },
        error: (error: any) => {
            console.error('Error during file upload:', error);
            notificationService.error(`Error during file upload ${error.message}`);
        },
        complete: () => {
            console.debug('File upload complete');
        },
    });
}


export async function exportUnshippedOrderFulfillments(action: string, orderIds: string[], format: string, dataService: DataService, notificationService: NotificationService) {
    // const dataService = context.injector.get(DataService);
    dataService.mutate<
        Mutation,
        MutationExportUnshippedOrdersToCsvArgs
    >(
        EXPORT_UNSHIPPED_ORDERS_TO_CSV, { input: { action, orderIds, format } }
    ).subscribe({
        next: (response: Mutation) => {
            if (response.exportUnshippedOrdersToCsv.error) {
                notificationService.error(response.exportUnshippedOrdersToCsv.error);
            } else {
                downloadFile(`unshipped-order-fulfillments-${new Date().toISOString()}.csv`, response.exportUnshippedOrdersToCsv.downloadURL)
                notificationService.info(`Export Unshipped Order Fulfillments Done`);
            }
        },
        error: (error: any) => {
            console.error(`Error during export:${error}`);
            notificationService.error(`Error during export ${error.message}`);
        },
        complete: () => {
            console.debug('Orders export complete');
        },
    });

}

export async function exportSelectedOrders(action: string, orderIds: string[], dataService: DataService, notificationService: NotificationService) {
    // const dataService = context.injector.get(DataService);
    dataService.mutate<
        Mutation,
        MutationExportOrdersToCsvArgs
    >(
        EXPORT_ORDERS_TO_CSV, { input: { action, orderIds } }
    ).subscribe({
        next: (response: Mutation) => {
            if (response.exportOrdersToCsv.error) {
                notificationService.error(response.exportOrdersToCsv.error);
            } else {
                downloadFile(`orders-${new Date().toISOString()}.csv`, response.exportOrdersToCsv.downloadURL)
                notificationService.info(`Export Orders Done`);
            }
        },
        error: (error: any) => {
            console.error(`Error during export:${error}`);
            notificationService.error(`Error during export ${error.message}`);
        },
        complete: () => {
            console.debug('Orders export complete');
        },
    });
}

export async function markOrdersAsDelivered(
    orderIds: string[],
    dataService: DataService,
    notificationService: NotificationService,
): Promise<boolean> {
    try {
        const response = await firstValueFrom(
            dataService.mutate(MARK_ORDERS_AS_DELIVERED, { orderIds })
        );
        const { updatedFulfillmentCount, skippedFulfillmentCount, errorCount } =
            (response as any).markOrdersAsDelivered;

        const parts: string[] = [`${updatedFulfillmentCount} fulfillment(s) marked as delivered.`];
        if (skippedFulfillmentCount > 0) {
            parts.push(`${skippedFulfillmentCount} skipped (not in Shipped state).`);
        }
        if (errorCount > 0) {
            parts.push(`${errorCount} error(s).`);
            notificationService.warning(parts.join(' '));
        } else {
            notificationService.success(parts.join(' '));
        }
        return updatedFulfillmentCount > 0;
    } catch (error: any) {
        notificationService.error(`Failed to mark orders as delivered: ${error.message}`);
        return false;
    }
}

export async function autoFulfillOrders(dataService: DataService, notificationService: NotificationService) {
    console.log('Auto Fulfill');
    dataService.mutate<
        Mutation
    >(
        AUTO_ORDER_FULFILLMENTS, {}
    ).subscribe({
        next: (response: Mutation) => {
            console.log('Auto Fulfill response', response);
            notificationService.info(`Auto Fulfill done, processed ${response.autoOrderFulfillments.processedOrderCount} order(s)`);
        },
        error: (error: any) => {
            console.error(`Error during auto fulfill:${error}`);
            notificationService.error(`Error during auto fulfill ${error.message}`);
        },
        complete: () => {
            console.debug('Auto fulfill complete');
        },
    });
}